import { useEffect, useMemo, useState } from 'react'
import usePreviewMode from '@broker-crm-common/hooks/usePreviewMode'
import { caseEligibleForClientReferral } from '@broker-crm-common/ReferClientModal/ReferClientModal.helpers'
import { useCaseContext } from '@broker-crm-contexts'
import { Box } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Feature, FlagType, hasPermission, useFlag, useFormatMessage, useUserContext } from '@acre/utils'
import { OrganisationType, useGetCaseVersionsTimelineLazyQuery, useGetOrganisationQuery } from '@acre/graphql'
import { Switch, SwitchVariant } from '@acre/design-system'

import DocumentNavigation from './DocumentNavigation'
import CaseInfo from './Info/CaseInfo'
import PageNotes from './PageNotes'
import ReferClient from './ReferClient/ReferClient'
import RelatedCases from './RelatedCases'
import Tasks from './Tasks'
import VersionNavigation from './VersionNavigation'
import { SectionWrapper } from './ReviewModeDrawer.styles'

const ReviewModeDrawer = () => {
  const navigate = useNavigate()
  const formatMessage = useFormatMessage()
  const location = useLocation()
  const { details } = useCaseContext()
  const user = useUserContext()

  const caseSidebarDrawerFlag = useFlag([FlagType.Feature, Feature.CaseSidebarDrawer], false)
  const inNetworkReferralsFlag = useFlag([FlagType.Feature, Feature.ReferralPartnerships], false)

  const networkId = user?.organisation?.representative_of
  const userOrgId = user?.organisation_id

  const isInNetworkReferralsPreviewFlagSetOnUser = hasPermission(user, Feature.ReferralPartnershipsPreview)

  const isAr = useMemo(
    () => user?.organisation?.type === OrganisationType.Ar && networkId !== userOrgId,
    [user?.organisation?.type, networkId, userOrgId],
  )

  // Only fetch network data for network name when user is in an AR org
  const { data: networkData } = useGetOrganisationQuery({
    variables: { id: networkId! },
    skip: !networkId || !isAr,
  })

  const hasPermissionForInNetworkReferrals = useMemo(() => {
    // If network has referrals disabled associated ARs should not be able to access referrals
    if (isAr) {
      const networkOrgInNetworkReferralsFlag = networkData?.organisation?.metadata?.find(
        (flag) => flag.name === 'feature.in_network_referrals',
      )?.bool_value

      return networkOrgInNetworkReferralsFlag ? inNetworkReferralsFlag : false
    }

    return inNetworkReferralsFlag
  }, [inNetworkReferralsFlag, isAr, networkData?.organisation?.metadata])

  // Only override if in_network_referrals flag is not set on the organisation
  const shouldUserReferralsFlagOverride = !inNetworkReferralsFlag && isInNetworkReferralsPreviewFlagSetOnUser

  const { active: previewModeEnabled } = usePreviewMode()

  const { caseId, version } = useParams<{ caseId: string; version?: string }>()

  const [reviewModeOn, setReviewModeOn] = useState(false)

  useEffect(() => {
    if (!location.pathname.includes(`cases/${caseId}/review-mode`)) {
      setReviewModeOn(false)
    } else {
      setReviewModeOn(true)
    }
  }, [caseId, location.pathname])

  const displayVersionNav = useMemo(
    () => location.pathname.includes(`cases/${caseId}/review-mode/version`),
    [caseId, location.pathname],
  )

  // Exclude mortgage grid page for esis docs
  const displayDocNav = useMemo(
    () =>
      location.pathname.includes(`cases/${caseId}/review-mode/documents/`) &&
      !location.pathname.includes(`cases/${caseId}/review-mode/documents/ESIS/list`),
    [caseId, location.pathname],
  )

  const displayToggle = useMemo(
    () => previewModeEnabled || caseSidebarDrawerFlag,
    [caseSidebarDrawerFlag, previewModeEnabled],
  )

  const eligibleForClientReferral = useMemo(
    () =>
      caseEligibleForClientReferral(
        details.preference_mortgage_reason,
        details.clients,
        Boolean(details.btl_through_company),
      ),
    [details.preference_mortgage_reason, details.clients, details.btl_through_company],
  )

  const displayClientProtectionReferral = useMemo(() => {
    return (hasPermissionForInNetworkReferrals || shouldUserReferralsFlagOverride) && eligibleForClientReferral
  }, [eligibleForClientReferral, hasPermissionForInNetworkReferrals, shouldUserReferralsFlagOverride])

  const [getCaseVerionsTimeline] = useGetCaseVersionsTimelineLazyQuery()

  const onSwitch = async (caseId: string) => {
    if (reviewModeOn) {
      setReviewModeOn(false)
      return navigate(`/cases/${caseId}/overview`)
    }

    const { data } = await getCaseVerionsTimeline({
      variables: { id: caseId! },
    })

    const versiontTimeline = data?.caseVersionsTimeline.timeline

    // Navigate to the last version
    if (versiontTimeline?.length) {
      setReviewModeOn(true)
      return navigate(
        `/cases/${caseId}/review-mode/version/${versiontTimeline[versiontTimeline.length - 1].version_to}/fact-find`,
      )
    }
  }

  return (
    <>
      <SectionWrapper>
        <Box mb={2}>
          <CaseInfo />
          {displayClientProtectionReferral && <ReferClient networkName={networkData?.organisation?.name} />}
        </Box>
        {displayToggle && (
          <Switch
            variant={SwitchVariant.Large}
            id="ToggleReviewMode"
            checked={reviewModeOn}
            disabled={false}
            onChange={() => caseId && onSwitch(caseId)}
            label={formatMessage(`cases.reviewMode.${reviewModeOn ? `reviewModeOn` : `reviewModeOff`}`)}
          />
        )}
      </SectionWrapper>
      {displayVersionNav && <VersionNavigation caseId={caseId!} version={version} />}
      {displayDocNav && <DocumentNavigation caseId={caseId!} />}
      {!reviewModeOn && (
        <>
          <PageNotes />
          <Tasks />
        </>
      )}
      <RelatedCases />
    </>
  )
}

export default ReviewModeDrawer
